import { apiSources } from "@/api/recruit/source.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiSourceSavePayload } from "@/types/recruit/source.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const sourcesKeys = createQueryKeys("sources", {
  all: null,
});

export const useSources = (enabled: boolean = true) => {
  return useQuery({
    ...sourcesKeys.all,
    queryFn: async () => {
      const result = await apiSources.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export interface SourceSaveParams {
  id: number;
  payload: ApiSourceSavePayload;
}

export const useSourceSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: SourceSaveParams) {
      const result =
        id == 0 ? await apiSources.create(payload) : await apiSources.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: sourcesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useSourceDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiSources.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: sourcesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
